import { useEffect, useState } from 'react';

/**
 * Mirrors the `mobile` mixin in common.scss: phones by width, plus
 * touch-primary devices (tablets in portrait) up to 1024px. Kept in sync by
 * hand — there is no build-time bridge from SCSS to TS, so if the mixin's
 * breakpoint moves, this string has to move with it.
 */
export const MOBILE_MEDIA_QUERY =
  '(max-width: 768px), (hover: none) and (pointer: coarse) and (max-width: 1024px)';

const supportsMatchMedia = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function';

/**
 * True below the app's mobile breakpoint. For layout that CSS alone can't
 * express — e.g. dropping columns out of an Ant table, where hiding cells with
 * `display: none` still leaves the table reserving their width.
 */
export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(() =>
    supportsMatchMedia() ? window.matchMedia(MOBILE_MEDIA_QUERY).matches : false
  );

  useEffect(() => {
    if (!supportsMatchMedia()) return;
    const mql = window.matchMedia(MOBILE_MEDIA_QUERY);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    // Re-read on mount: the viewport can have changed between the initial
    // state and this effect (and SSR/jsdom starts from the `false` default).
    setIsMobile(mql.matches);
    // Safari < 14 only has the deprecated addListener form.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  return isMobile;
}

export default useIsMobile;
